import { useReduxState } from 'hooks';
import { AIGenerateContentButton } from 'components/aiTools';
import { Button, ConfirmButton, CopyButton } from 'components/common';

import type { AllBlocks } from 'shared-types';
import type { EditorBlockTypes } from 'types';
import type { EditorStateProps } from './EditorBlock';

type StateProps = Pick<
  EditorStateProps,
  'editorChapter' | 'editorBlockIsMoving'
>;

interface Props extends StateProps {
  blockData: AllBlocks;
  blockId: number;
  blockType: EditorBlockTypes;
  isDeleteView: boolean;
  isSelected: boolean;
  isVisible: boolean;
  onDelete: () => void;
  onVisibilityClick: () => void;
  toggleDeleteView: () => void;
  toggleMoveView: () => void;
}

const EditorBlockControls = ({
  blockData,
  blockId,
  blockType,
  isDeleteView,
  isSelected,
  isVisible,
  onDelete,
  onVisibilityClick,
  toggleDeleteView,
  toggleMoveView,
  ...stateProps
}: Props) => {
  const { editorChapter, editorBlockIsMoving } = stateProps;

  const {
    messages,
    serverConfig: { readerUrl },
    pageState: { course }
  } = useReduxState();

  const isMoving = editorBlockIsMoving && isSelected;

  return (
    <div className="editor-block-controls">
      {isDeleteView ? (
        <div>
          {Object.keys(course.languageVersions).length > 0 ? (
            <ConfirmButton
              actionText={messages.commonDelete}
              buttonCategory="danger"
              buttonIcon="trash-alt"
              buttonSize="small"
              buttonStyle={{ marginRight: 5 }}
              buttonText={messages.commonDelete}
              category="danger"
              confirmText={messages.courseLanguageVersionsDeleteAllWarning}
              confirmTitle={messages.editorHeaderDeleteBlocks}
              onConfirm={() => {
                onDelete();
                return true;
              }}
            />
          ) : (
            <Button
              category="danger"
              icon="trash-alt"
              onClick={onDelete}
              size="small"
              style={{ marginRight: 5 }}
              text={messages.commonDelete}
            />
          )}
          <Button
            category="light"
            onClick={toggleDeleteView}
            size="small"
            text={messages.commonCancel}
          />
        </div>
      ) : (
        <div>
          <AIGenerateContentButton
            blockData={blockData}
            blockId={blockId}
            style={{ marginRight: 5 }}
            type={blockType}
          />

          <CopyButton
            buttonStyle={{ height: 28, padding: '6px 0 7px', width: 28 }}
            category="light"
            data-tooltip={messages.editorBlockControlsCopy}
            icon="link"
            successText={messages.commonCopied}
            value={`${readerUrl}/courses/${course.slug}/${editorChapter.slug}#${blockId}`}
            size="small"
            style={{ marginRight: 5 }}
          />

          <Button
            category={isMoving ? 'default' : 'light'}
            data-tooltip={messages.editorBlockControlsMove}
            icon="arrows-alt-v"
            onClick={toggleMoveView}
            size="small"
            style={{ marginRight: 5 }}
          />

          <Button
            category="light"
            data-tooltip={
              isVisible ? messages.commonHide : messages.commonUnhide
            }
            icon={isVisible ? 'eye' : 'eye-slash'}
            onClick={onVisibilityClick}
            size="small"
            style={
              isVisible ? { marginRight: 5 } : { color: '#b41', marginRight: 5 }
            }
          />
          <Button
            category="light"
            data-tooltip={messages.commonDelete}
            icon="trash-alt"
            onClick={toggleDeleteView}
            size="small"
          />
        </div>
      )}

      <style jsx>{`
        .editor-block-controls {
          bottom: 10px;
          right: 10px;
          position: absolute;
          visibility: hidden;
          z-index: 1;
        }
      `}</style>
    </div>
  );
};

export default EditorBlockControls;
